//* 장지 1차·2차 구분 — 2026-11-02
//* - 저장: deceasedInfo.cemeteries = [{ label, name }] (백엔드 스키마 요청, docs/fevent-cemeteries-spec.md)
//*   + 기존 deceasedInfo.cemetery 문자열에도 "1차 → 2차"로 합성 저장 → 구서버(배열을 strict로 버림)·문자열만 보는 소비처 그대로 동작
//* - 라벨 기본값 "1차 장지"/"2차 장지", 사용자가 "화장장"/"장지"처럼 바꿀 수 있음(빈칸이면 기본값 저장)
//* - 표시: 배열이 있으면 라벨별 줄, 없으면 옛 문자열("OOO->OOO")을 "장지" 한 줄로 그대로
//* - 수정 폼 시딩: 배열은 모두 유지하고, 옛 문자열의 화살표(->, →, =>)는 행으로 나눈다.

export interface FeventCemetery {
  label: string;
  name: string;
}

export type CemeteryFormRow = FeventCemetery;

export const CEMETERY_ROW_COUNT = 2;
export const CEMETERY_MAX_INPUT_ROWS = 3;
export const CEMETERY_DEFAULT_LABELS = ['1차 장지', '2차 장지'] as const;
export const CEMETERY_LABEL_MAX_LENGTH = 10;

export const cemeteryDefaultLabel = (index: number): string =>
  CEMETERY_DEFAULT_LABELS[index] ?? `${index + 1}차 장지`;

const isDefaultLabel = (label: string): boolean => /^\d+차 장지$/.test(label);

/** 장지 중복은 허용하고, 이름표가 화장장인 행만 한 곳으로 제한한다. */
export const validateCemeteryRows = (rows: CemeteryFormRow[] = []): string | null =>
  rows.filter((row) => row?.label?.trim() === '화장장').length > 1
    ? '화장장은 한 곳만 선택해 주세요.'
    : null;

/** 입력 중 자동 이름표. 빈 두 번째 칸은 추가 입력용으로 "2차 장지"를 유지한다. */
export const cemeteryFormDefaultLabel = (
  index: number,
  rows: CemeteryFormRow[] = [],
): string => {
  const filledCount = rows.filter((row) => row?.name?.trim()).length;
  if (
    (filledCount === 0 && index === 0) ||
    (filledCount === 1 && rows[index]?.name?.trim())
  ) {
    return '장지';
  }
  return cemeteryDefaultLabel(index);
};

const ARROW = /\s*(?:->|→|=>|⇒)\s*/;

const emptyRows = (): CemeteryFormRow[] =>
  Array.from({ length: CEMETERY_ROW_COUNT }, () => ({ label: '', name: '' }));

/** 기본 2줄. 저장된 추가 행은 자르지 않으며 기본 라벨은 placeholder로 표시한다. */
export const toCemeteryFormRows = (info?: {
  cemetery?: string;
  cemeteries?: FeventCemetery[];
}): CemeteryFormRow[] => {
  const rows = emptyRows();
  const structured = (info?.cemeteries ?? []).filter((c) => c?.name?.trim());
  if (structured.length > 0) {
    structured.forEach((c, i) => {
      const label = (c.label ?? '').trim();
      rows[i] = {
        label: isDefaultLabel(label) ? '' : label,
        name: c.name.trim(),
      };
    });
    return rows;
  }
  const legacy = (info?.cemetery ?? '').trim();
  if (!legacy) return rows;
  const parts = legacy.split(ARROW).map((p) => p.trim());
  if (parts.length >= 2 && parts.every(Boolean)) {
    parts.forEach((name, index) => {
      rows[index] = { label: '', name };
    });
  } else {
    rows[0] = { label: '', name: legacy };
  }
  return rows;
};

/** 저장용 배열: 이름이 있는 줄만, 라벨 빈칸은 기본값으로 확정 저장 */
export const toCemeteriesPayload = (rows?: CemeteryFormRow[]): FeventCemetery[] =>
  (rows ?? []).flatMap((row, i) => {
    const name = (row?.name ?? '').trim();
    if (!name) return [];
    const label = (row?.label ?? '').trim().slice(0, CEMETERY_LABEL_MAX_LENGTH);
    return [{ label: label || cemeteryDefaultLabel(i), name }];
  });

/** 구서버·문자열 소비처용 합성 문자열 "1차 → 2차" */
export const composeLegacyCemetery = (cemeteries: FeventCemetery[]): string =>
  cemeteries.map((c) => c.name).join(' → ');

export type CemeteryDisplayRow = FeventCemetery;

/** 부고장·관리자 표시 줄. 배열 1건이고 라벨이 기본값이면 그냥 "장지" */
export const cemeteryDisplayRows = (info?: {
  cemetery?: string;
  cemeteries?: FeventCemetery[];
}): CemeteryDisplayRow[] => {
  const structured = (info?.cemeteries ?? []).filter((c) => c?.name?.trim());
  if (structured.length === 1) {
    const only = structured[0] as FeventCemetery;
    const label = (only.label ?? '').trim();
    const isDefault = isDefaultLabel(label) || !label;
    return [{ label: isDefault ? '장지' : label, name: only.name.trim() }];
  }
  if (structured.length > 1) {
    return structured.map((c, i) => ({
      label: (c.label ?? '').trim() || cemeteryDefaultLabel(i),
      name: c.name.trim(),
    }));
  }
  const legacy = (info?.cemetery ?? '').trim();
  return legacy ? [{ label: '장지', name: legacy }] : [];
};
